import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { StripeProvider, Elements } from 'react-stripe-elements';

import Popup from '../../Popup/PopupV2';
import Img from '../../Img';
import config from '../../../../config';
import { paymentIconsDictionary, PopupTypes } from '../../../utils/constants';
import { getMonthlyToAnnualUpgradeDetails } from '../../../api/checkoutApi';
import { getApiAxiosInstance } from '../../../utils/network';
import { closePopup, showPopupNew } from '../../../actions/popup-actions';
import { submitMonthlyToAnnualUpgrade } from '../../../actions/checkout-subscription-actions';
import { trackEvent } from '../../Analytics/TrackedClick';
import {
    SubscriptionTypes,
    SubscriptionTerms,
    UpgradeFunnelEvents,
    PowerFunnelSurfaces,
    SubscriptionGateways,
} from '../../Analytics/constants';
import { classifyFailureType } from '../../Analytics/classifyFailureType';
import ChangeCardForm from './ChangeCardForm';
import {
    MONTHLY_TO_ANNUAL_CONTENT,
    CARD_BRAND_ICON_MAP,
    ACCEPTED_CARD_BRANDS,
} from './constants';

import styles from './upgradeMonthlyToAnnual.scss';
import popupStyles from '../../Popup/popupv2.scss';
import { usePowerFunnelTracking } from '../../hooks/usePowerFunnelTracking';
import useFabletics from '../../hooks/useFabletics';

const getBaseTrackingProps = (referringSurface) => ({
    subscription_type: SubscriptionTypes.POWER,
    plan_from: SubscriptionTerms.MONTHLY,
    plan_to: SubscriptionTerms.ANNUAL,
    referringSurface: referringSurface || PowerFunnelSurfaces.UNKNOWN,
});

// gatedFeature is accepted for analytics/funnel tracking and routed to usePowerFunnelTracking;
// the monthly→annual popup intentionally renders the same content regardless of feature context.
const UpgradePowerMonthlyToAnnualPopup = ({ onCloseClick, referringSurface, gatedFeature, prismicTitle }) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.auth?.user);
    const isAppleSubscriber = user?.subscriptionGateway === SubscriptionGateways.APPLE;
    const isStripeSubscriber = user?.subscriptionGateway === SubscriptionGateways.STRIPE;
    const { enabled: isFableticsEnabled } = useFabletics();

    const [stripe, setStripe] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(null);
    const [upgradeError, setUpgradeError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [showChangeCardForm, setShowChangeCardForm] = useState(false);

    const [upgradeData, setUpgradeData] = useState(null);
    const [binCountryError, setBinCountryError] = useState('');

    const { trackPowerPaywallViewed, trackPowerCheckoutStarted, trackPowerPurchaseSucceeded } = usePowerFunnelTracking({ referringSurface, gatedFeature, prismicTitle });

    useEffect(() => {
        if (window.Stripe) {
            setStripe(window.Stripe(config.stripeKey));
        } else {
            const el = document.querySelector('#stripe-js');
            if (el) {
                el.addEventListener('load', () => setStripe(window.Stripe(config.stripeKey)));
            }
        }

        trackPowerPaywallViewed();
    }, []);

    const fetchUpgradeData = async () => {
        setLoading(true);
        setFetchError(null);
        try {
            const response = await getMonthlyToAnnualUpgradeDetails(getApiAxiosInstance());
            const data = response?.data;
            if (data) {
                setUpgradeData(data);
            } else {
                setFetchError('Unable to load upgrade details');
            }
        } catch (err) {
            setFetchError(err?.response?.data?.message || 'Unable to load upgrade details');
            setUpgradeData(null);
        } finally {
            setLoading(false);
        }
    };

    const [hasInitialized, setHasInitialized] = useState(false);

    useEffect(() => {
        if (!user || hasInitialized) {
            return;
        }

        setHasInitialized(true);

        if (isStripeSubscriber) {
            fetchUpgradeData();
        } else {
            setLoading(false);
        }
    }, [user, isStripeSubscriber, hasInitialized]);

    const planId = upgradeData?.annualPlan?.planId;
    const plan = upgradeData?.annualPlan;

    const handleBinCountryMismatch = (annualPlanData, errorMessage) => {
        setUpgradeData((prev) => {
            const next = { ...(prev || {}) };
            if (annualPlanData?.planId) {
                next.annualPlan = annualPlanData;
            } else if (annualPlanData?.annualPlan) {
                next.annualPlan = annualPlanData.annualPlan;
            }
            if (annualPlanData?.isTennisAustraliaMember !== undefined) {
                next.isTennisAustraliaMember = annualPlanData.isTennisAustraliaMember;
            }
            return next;
        });
        setBinCountryError(errorMessage);
        setUpgradeError('');
    };

    const performUpgrade = async (paymentMethodId = null) => {
        if (!planId) return;
        setSubmitting(true);
        setUpgradeError('');
        setBinCountryError('');

        trackPowerCheckoutStarted({
            billingCycle: SubscriptionTerms.ANNUAL,
            priceLocal: plan?.amountPayPerRecurringInterval,
            currency: plan?.currency || 'USD',
        });

        try {
            const result = await Promise.resolve(dispatch(submitMonthlyToAnnualUpgrade({
                values: { planId, paymentMethodId },
                onBinCountryMismatch: handleBinCountryMismatch,
                stripe,
            })));

            if (result?.isSubscribed) {
                trackPowerPurchaseSucceeded({
                    billingCycle: SubscriptionTerms.ANNUAL,
                    priceLocal: plan?.amountPayPerRecurringInterval,
                    currency: plan?.currency || 'USD',
                });

                const renewalDate = upgradeData?.renewalDate
                    ? moment(upgradeData.renewalDate).format('MMM D, YYYY')
                    : '';
                dispatch(closePopup(PopupTypes.POWER_MONTHLY_UPGRADE_POPUP));
                dispatch(showPopupNew({
                    popup: PopupTypes.POWER_MONTHLY_UPGRADE_SUCCESS_POPUP,
                    renewalDate,
                }));
            } else if (result?.error) {
                trackEvent(UpgradeFunnelEvents.UPGRADE_FAILED, {
                    ...getBaseTrackingProps(referringSurface),
                    failure_type: classifyFailureType(result.error),
                });
                setUpgradeError(result.error);
            }
        } catch (err) {
            const responseData = err?.response?.data;
            if (responseData?.annualPlan) {
                handleBinCountryMismatch(
                    responseData.annualPlan,
                    responseData?.message || 'We have changed the billing details to match your billing country, please try again.',
                );
            } else {
                const message = responseData?.message || err?.message || 'Something went wrong. Please update your card below.';
                trackEvent(UpgradeFunnelEvents.UPGRADE_FAILED, {
                    ...getBaseTrackingProps(referringSurface),
                    failure_type: classifyFailureType(message),
                });
                setUpgradeError(message);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpgradeClick = () => {
        trackEvent(UpgradeFunnelEvents.CTA_CLICKED, getBaseTrackingProps(referringSurface));
        performUpgrade(null);
    };

    const handleChangeCard = () => {
        trackEvent(UpgradeFunnelEvents.CHANGE_CARD_CLICKED, getBaseTrackingProps(referringSurface));
        setUpgradeError('');
        setShowChangeCardForm(true);
    };

    const renderCardBrands = () => (
        <div className={styles.cardsRow}>
            <span className={styles.cardsText}>{MONTHLY_TO_ANNUAL_CONTENT.cardBrandsText}</span>
            {ACCEPTED_CARD_BRANDS.map((brand) => (
                <Img key={brand} src={CARD_BRAND_ICON_MAP[brand]} alt={brand} />
            ))}
        </div>
    );

    const handleUseCardOnFile = () => {
        setBinCountryError('');
        setShowChangeCardForm(false);
        fetchUpgradeData();
    };

    const handleNewCardSuccess = (newPaymentMethodId) => {
        trackEvent(UpgradeFunnelEvents.CTA_CLICKED, getBaseTrackingProps(referringSurface));
        performUpgrade(newPaymentMethodId);
    };

    const modalHeader = () => (
        <div className={`${popupStyles.modalHeader} ${styles.header}`}>
            <div className={styles.headerSpacer} />
            <p className={styles.headerTitle}>
                Upgrade to an Annual Power Membership
            </p>
            <div
                className={`${popupStyles.btnClose} ${styles.closeBtn}`}
                onClick={onCloseClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onCloseClick()}
            >
                <i className="utr utr-close" />
            </div>
        </div>
    );

    if (!user || loading) {
        return (
            <div className={`${styles.popup} overlay lite-black`}>
                <Popup
                    className="popup-lg"
                    config={{
                        size: 'lg',
                        scrollable: true,
                        customHeader: modalHeader,
                        customWidth: '840px',
                        bodyClassName: styles.modalBodyMobile,
                    }}
                    onCloseClick={onCloseClick}
                >
                    <div className={styles.loadingState}>Loading...</div>
                </Popup>
            </div>
        );
    }

    if (isAppleSubscriber) {
        return (
            <div className={`${styles.popup} overlay lite-black`}>
                <Popup
                    className="popup-lg"
                    config={{
                        size: 'lg',
                        scrollable: true,
                        customHeader: modalHeader,
                        customWidth: '840px',
                        bodyClassName: styles.modalBodyMobile,
                    }}
                    onCloseClick={onCloseClick}
                >
                    <div className={styles.appleSubscriberMessage}>
                        <h3 className={styles.appleTitle}>
                            Your Power subscription was purchased through Apple.
                        </h3>
                        <p className={styles.appleText}>
                            To upgrade to Annual Power, please open the UTR Sports app on your iPhone or iPad
                            and upgrade your subscription there.
                        </p>
                        <p className={styles.appleText}>
                            You can also manage your subscription in Apple Settings → Subscriptions.
                        </p>
                        <button
                            type="button"
                            className={`btn btn-primary text-uppercase ${styles.closeButton}`}
                            onClick={onCloseClick}
                        >
                            Got it
                        </button>
                    </div>
                </Popup>
            </div>
        );
    }

    if (fetchError) {
        return (
            <div className={`${styles.popup} overlay lite-black`}>
                <Popup
                    className="popup-lg"
                    config={{
                        size: 'lg',
                        scrollable: true,
                        customHeader: modalHeader,
                        customWidth: '840px',
                        bodyClassName: styles.modalBodyMobile,
                    }}
                    onCloseClick={onCloseClick}
                >
                    <div className={styles.errorState}>{fetchError}</div>
                </Popup>
            </div>
        );
    }

    const paymentSource = upgradeData?.paymentSource;
    const isTA = upgradeData?.isTennisAustraliaMember ?? false;
    const formattedRenewal = upgradeData?.renewalDate
        ? moment(upgradeData.renewalDate).format('MMM D, YYYY')
        : '';

    const displayAmount = plan?.displayAmountPayPerRecurringInterval ?? plan?.displayAnnualAmount;
    const displayMonthly = plan?.displayMonthlyAmount;
    const cardholderName = paymentSource?.cardholderName ?? '';
    const last4 = paymentSource?.last4 ?? '';
    const cardType = paymentSource?.type ?? '';
    const dots = cardType.toLowerCase() === 'american express' ? '**** ****** *' : '**** **** **** ';

    const content = (
        <div className={styles.content}>
                    <div className={styles.leftColumn}>
                        <div className={styles.sectionTitle}>Upgrade to an Annual Power Plan</div>
                        {isTA && (
                            <div className={styles.taMessage}>
                                <div className={styles.taLogo}>
                                    <img src="/img/social/tennis-australia-logo.svg" alt="Tennis Australia" />
                                </div>
                                <span>
                                    Tennis Australia members receive the best annual pricing. Save over 41% on Power.
                                </span>
                            </div>
                        )}
                        <div className={styles.planCard}>
                            <div className={styles.planCardLeft}>
                                <div className={styles.planPrice}>
                                    {displayAmount}
                                </div>
                                <div className={styles.planMonthly}>
                                    {displayMonthly}/Month
                                </div>
                            </div>
                            {formattedRenewal && (
                                <div className={styles.planCardRight}>
                                    <div className={styles.renewalDate}>
                                        Renewal Date: <strong>{formattedRenewal}</strong>
                                    </div>
                                </div>
                            )}
                        </div>

                        {binCountryError && (
                            <div className={styles.binCountryError}>
                                <div className={styles.binCountryErrorTitle}>
                                    Billing Country Does Not Match Your Residence
                                </div>
                                <div className={styles.binCountryErrorText}>
                                    Our subscription pricing is based on your billing country. The rates have been updated accordingly.
                                </div>
                            </div>
                        )}
                        <p className={styles.rolloverText}>
                            {MONTHLY_TO_ANNUAL_CONTENT.prorationMessage}
                        </p>
                        {isFableticsEnabled && (
                            <p className={styles.rolloverText}>
                                USA billing ZIP required to access Fabletics.
                            </p>
                        )}
                    </div>
                    <div className={styles.rightColumn}>
                        {upgradeError && (
                            <div className={styles.upgradeError}>
                                {upgradeError}
                            </div>
                        )}
                        {showChangeCardForm && stripe ? (
                            <div className={styles.formSection}>
                                <Elements>
                                        <ChangeCardForm
                                            onSuccess={handleNewCardSuccess}
                                            onError={setUpgradeError}
                                            submitting={submitting}
                                            isTennisAustraliaMember={isTA}
                                            savingsPercentage={plan?.savingsPercentage ?? 0}
                                            formStyles={{
                                                formTitle: styles.formTitle,
                                                changeCardLink: styles.changeCardLink,
                                                upgradeButton: `btn btn-primary text-uppercase btn-block ${styles.upgradeButtonSpacing}`,
                                                disclaimer: styles.disclaimer,
                                            }}
                                            onUseCardOnFile={handleUseCardOnFile}
                                            renderCardBrands={renderCardBrands}
                                        />
                                </Elements>
                            </div>
                        ) : (
                            <div className={styles.rightColumnContent}>
                                <div className={styles.sectionTitle}>{MONTHLY_TO_ANNUAL_CONTENT.paymentMethodTitle}</div>
                                <div className={styles.paymentCard}>
                                    {cardholderName && (
                                        <div className={styles.cardholderName}>{cardholderName}</div>
                                    )}
                                    {last4 && (
                                        <div className={styles.maskedNumber}>{dots}{last4}</div>
                                    )}
                                    {cardType && (
                                        <div className={styles.cardBrand}>
                                            <Img
                                                className="payment-icon"
                                                src={`/img/payment/${paymentIconsDictionary[cardType?.toLowerCase()]}`}
                                            />
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className={styles.changeCardLink}
                                    onClick={handleChangeCard}
                                >
                                    {MONTHLY_TO_ANNUAL_CONTENT.changeCard}
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-primary text-uppercase btn-block ${styles.upgradeButtonSpacing}`}
                                    onClick={handleUpgradeClick}
                                    disabled={submitting}
                                >
                                    {submitting ? MONTHLY_TO_ANNUAL_CONTENT.ctaProcessing : MONTHLY_TO_ANNUAL_CONTENT.ctaText}
                                </button>
                                <div className={styles.disclaimer}>
                                    {MONTHLY_TO_ANNUAL_CONTENT.disclaimer.prefix}
                                    <a href={MONTHLY_TO_ANNUAL_CONTENT.disclaimer.termsHref} target="_blank" rel="noopener noreferrer">
                                        {MONTHLY_TO_ANNUAL_CONTENT.disclaimer.termsLink}
                                    </a>
                                    {MONTHLY_TO_ANNUAL_CONTENT.disclaimer.suffix}
                                </div>
                            </div>
                        )}
                    </div>
        </div>
    );

    return (
        <div className={`${styles.popup} overlay lite-black`}>
            <Popup
                className="popup-lg"
                config={{
                    size: 'lg',
                    scrollable: false,
                    customHeader: modalHeader,
                    customWidth: '840px',
                    bodyClassName: styles.modalBodyMobile,
                }}
                onCloseClick={onCloseClick}
            >
                {stripe ? (
                    <StripeProvider stripe={stripe}>
                        {content}
                    </StripeProvider>
                ) : (
                    content
                )}
            </Popup>
        </div>
    );
};

UpgradePowerMonthlyToAnnualPopup.propTypes = {
    onCloseClick: PropTypes.func.isRequired,
    referringSurface: PropTypes.string.isRequired,
    gatedFeature: PropTypes.string,
    prismicTitle: PropTypes.string,
};

UpgradePowerMonthlyToAnnualPopup.defaultProps = {
    gatedFeature: null,
    prismicTitle: null,
};

export default UpgradePowerMonthlyToAnnualPopup;
